/**
 * useOwnerActivity Hook
 * 오너 활동 모니터링 커스텀 훅
 */

import { useState, useCallback, useEffect } from 'react';
import { ownerActivityRepository } from '../repositories/OwnerActivityRepository';
import {
  OwnerActivityLog,
  InactiveOwner,
  OwnerActivitySummary,
  OwnerLastActivity,
} from '../models';

export const useOwnerActivity = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inactiveOwners, setInactiveOwners] = useState<InactiveOwner[]>([]);
  const [activitySummary, setActivitySummary] = useState<OwnerActivitySummary | null>(null);
  const [ownersLastActivity, setOwnersLastActivity] = useState<Map<string, OwnerLastActivity>>(new Map());

  /**
   * 비활성 오너 목록 조회
   */
  const fetchInactiveOwners = useCallback(async (days = 7) => {
    setLoading(true);
    setError(null);

    try {
      const data = await ownerActivityRepository.getInactiveOwners(days);
      setInactiveOwners(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '비활성 오너 조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 활동 요약 조회
   */
  const fetchActivitySummary = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await ownerActivityRepository.getActivitySummary();
      setActivitySummary(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '활동 요약 조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 모든 오너의 마지막 활동 정보 조회
   */
  const fetchAllOwnersLastActivity = useCallback(async () => {
    try {
      const data = await ownerActivityRepository.getAllOwnersLastActivity();
      const activityMap = new Map<string, OwnerLastActivity>();
      data.forEach((activity) => {
        // owner_id와 organization_id 둘 다로 조회할 수 있도록 저장
        activityMap.set(activity.owner_id, activity);
        if (activity.organization_id) {
          activityMap.set(activity.organization_id, activity);
        }
      });
      setOwnersLastActivity(activityMap);
      return activityMap;
    } catch (err) {
      console.error('Failed to fetch owners last activity:', err);
      return new Map<string, OwnerLastActivity>();
    }
  }, []);

  /**
   * 특정 오너/조직의 마지막 활동 정보 가져오기
   */
  const getOwnerLastActivity = useCallback(
    (ownerId: string): OwnerLastActivity | undefined => {
      return ownersLastActivity.get(ownerId);
    },
    [ownersLastActivity]
  );

  return {
    loading,
    error,
    inactiveOwners,
    activitySummary,
    ownersLastActivity,
    fetchInactiveOwners,
    fetchActivitySummary,
    fetchAllOwnersLastActivity,
    getOwnerLastActivity,
  };
};

/**
 * 특정 오너의 활동 로그 조회 훅
 */
export const useOwnerActivityLogs = (ownerId: string) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [logs, setLogs] = useState<OwnerActivityLog[]>([]);
  const [hasMore, setHasMore] = useState(true);

  const fetchLogs = useCallback(
    async (offset = 0, limit = 50) => {
      if (!ownerId) return;

      setLoading(true);
      setError(null);

      try {
        const data = await ownerActivityRepository.getOwnerActivityLogs(ownerId, limit, offset);
        if (offset === 0) {
          setLogs(data);
        } else {
          setLogs((prev) => [...prev, ...data]);
        }
        setHasMore(data.length === limit);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '활동 로그 조회에 실패했습니다.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    },
    [ownerId]
  );

  // 초기 로드
  useEffect(() => {
    if (ownerId) {
      fetchLogs(0);
    }
  }, [ownerId, fetchLogs]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchLogs(logs.length);
    }
  }, [loading, hasMore, logs.length, fetchLogs]);

  return {
    loading,
    error,
    logs,
    hasMore,
    fetchLogs,
    loadMore,
  };
};
